import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import Section from '../ui/Section';
import StatusChip from '../ui/StatusChip';
import { describeStatus, type BillingSummary } from './billingModel';

export interface PlanAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Label while the action runs. */
  busyLabel?: string;
  busy?: boolean;
}

interface CurrentPlanSectionProps {
  summary: BillingSummary;
  /** The one primary action: Upgrade, Change plan, Keep <plan> or Update card. */
  primaryAction: PlanAction | null;
  /** Shown only when the subscription can be cancelled. */
  onCancel?: () => void;
  cancelDisabled?: boolean;
  onShowHistory: () => void;
  /** A line under the actions, e.g. why plan changes wait for "Keep Pro". */
  footnote?: string | null;
}

/**
 * The plan the account is on, its status in one sentence, and what can be
 * done about it: one primary action, plus "Cancel subscription" only for a
 * subscription that bills and isn't already ending.
 */
const CurrentPlanSection: React.FC<CurrentPlanSectionProps> = ({
  summary,
  primaryAction,
  onCancel,
  cancelDisabled,
  onShowHistory,
  footnote,
}) => {
  const status = describeStatus(summary);

  return (
    <Section
      title="Current plan"
      action={
        <Button
          size="small"
          color="inherit"
          startIcon={<HistoryOutlinedIcon />}
          onClick={onShowHistory}
          // On phones the action wraps under the title; line its icon up with the title.
          sx={{ ml: { xs: -1.5, sm: 0 } }}
        >
          Plan history
        </Button>
      }
      sx={{ height: '100%' }}
    >
      <Typography
        variant="h4"
        component="p"
        data-testid="current-plan-name"
        sx={{ fontSize: '1.5rem', lineHeight: 1.25, mb: 1 }}
      >
        {summary.planName}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1, rowGap: 0.75 }}>
        {status.chip && (
          <StatusChip kind="billing" status={status.chip.status} label={status.chip.label} tone={status.chip.tone} />
        )}
        <Typography
          variant="body2"
          data-testid="billing-status"
          sx={{ color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}
        >
          {/* Each phrase stays on one line, so a narrow screen never strands "tax". */}
          {status.text.split(' · ').map((part, index) => (
            <React.Fragment key={`${index}-${part}`}>
              {index > 0 && ' · '}
              <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                {part}
              </Box>
            </React.Fragment>
          ))}
        </Typography>
      </Box>

      {status.detail && (
        <Typography variant="body2" color="text.secondary" data-testid="billing-detail" sx={{ mt: 1, maxWidth: 640 }}>
          {status.detail}
        </Typography>
      )}

      {(primaryAction || (summary.canCancel && onCancel)) && (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 2.5 }}>
          {primaryAction && (
            <Button variant="contained" onClick={primaryAction.onClick} disabled={primaryAction.disabled || primaryAction.busy}>
              {primaryAction.busy ? primaryAction.busyLabel || 'Working…' : primaryAction.label}
            </Button>
          )}
          {summary.canCancel && onCancel && (
            <Button variant="outlined" color="inherit" onClick={onCancel} disabled={cancelDisabled}>
              Cancel subscription
            </Button>
          )}
        </Box>
      )}

      {footnote && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          {footnote}
        </Typography>
      )}
    </Section>
  );
};

export default CurrentPlanSection;
